import { RefObject, useEffect } from 'react'

type UseHorizontalDragScrollOptions = {
  disabled?: boolean
}

const INTERACTIVE_SELECTOR =
  'button, input, select, textarea, [data-drag-scroll-ignore]'

export const useHorizontalDragScroll = (
  ref: RefObject<HTMLElement | null>,
  { disabled = false }: UseHorizontalDragScrollOptions = {}
) => {
  useEffect(() => {
    const element = ref.current

    if (!element || disabled) {
      return
    }

    let isPointerDown = false
    let isDragging = false
    let startX = 0
    let scrollLeft = 0
    let pointerId: number | null = null
    let preventNextClick = false
    let hasScrolled = false

    const setDraggingState = (value: boolean) => {
      if (value) {
        element.dataset.dragging = 'true'
        return
      }

      delete element.dataset.dragging
    }

    const resetStyles = () => {
      element.style.removeProperty('user-select')
      element.style.removeProperty('cursor')
      setDraggingState(false)
    }

    const releasePointerCapture = () => {
      if (pointerId != null && element.hasPointerCapture(pointerId)) {
        element.releasePointerCapture(pointerId)
      }
      pointerId = null
    }

    const handlePointerDown = (event: PointerEvent) => {
      if (disabled) {
        return
      }

      if (event.pointerType !== 'mouse') {
        return
      }

      if (event.button !== 0) {
        return
      }

      const target = event.target as HTMLElement | null
      if (target && target.closest(INTERACTIVE_SELECTOR)) {
        return
      }

      if (element.scrollWidth <= element.clientWidth) {
        return
      }

      isPointerDown = true
      isDragging = false
      preventNextClick = false
      hasScrolled = false
      startX = event.clientX
      scrollLeft = element.scrollLeft
      pointerId = event.pointerId
    }

    const handlePointerMove = (event: PointerEvent) => {
      if (!isPointerDown) {
        return
      }

      const deltaX = event.clientX - startX
      const movement = Math.abs(deltaX)

      if (!isDragging) {
        const DRAG_THRESHOLD = 8
        if (movement < DRAG_THRESHOLD) {
          return
        }
        isDragging = true

        if (pointerId != null && !element.hasPointerCapture(pointerId)) {
          try {
            element.setPointerCapture(pointerId)
          } catch (error) {
            console.error('Pointer capture is not supported for this browser')
          }
        }

        if (event.pointerType === 'mouse') {
          element.style.cursor = 'grabbing'
          element.style.userSelect = 'none'
        }

        setDraggingState(true)
      }

      if (movement === 0) {
        return
      }

      const previousScrollLeft = element.scrollLeft
      const nextScrollLeft = scrollLeft - deltaX

      element.scrollLeft = nextScrollLeft

      if (!hasScrolled && element.scrollLeft !== previousScrollLeft) {
        hasScrolled = true
        preventNextClick = true
      }

      if (hasScrolled) {
        event.preventDefault()
      }
    }

    const endDrag = () => {
      if (!isPointerDown) {
        return
      }

      releasePointerCapture()
      resetStyles()

      isPointerDown = false
      isDragging = false
      hasScrolled = false
      pointerId = null
    }

    const handlePointerUp = () => {
      endDrag()
    }

    const handlePointerCancel = () => {
      endDrag()
    }

    const handleClick = (event: MouseEvent) => {
      if (!preventNextClick) {
        return
      }

      preventNextClick = false
      event.stopPropagation()
      event.preventDefault()
    }

    element.addEventListener('pointerdown', handlePointerDown)
    element.addEventListener('pointermove', handlePointerMove)
    element.addEventListener('pointerup', handlePointerUp)
    element.addEventListener('pointerleave', handlePointerUp)
    element.addEventListener('pointercancel', handlePointerCancel)
    element.addEventListener('click', handleClick, true)

    return () => {
      element.removeEventListener('pointerdown', handlePointerDown)
      element.removeEventListener('pointermove', handlePointerMove)
      element.removeEventListener('pointerup', handlePointerUp)
      element.removeEventListener('pointerleave', handlePointerUp)
      element.removeEventListener('pointercancel', handlePointerCancel)
      element.removeEventListener('click', handleClick, true)
      releasePointerCapture()
      resetStyles()
    }
  }, [ref, disabled])
}
